<template>
  <div v-if="technician" class="modal-overlay">
    <div class="detail-container" @click.stop>
      <div class="modal-header">
        <h1 class="detail-title">Technician Detail</h1>
        <button class="close-btn" @click="handleClose">
          <img :src="closeFillIcon" alt="close" class="close-icon" />
        </button>
      </div>

      <div class="modal-body">
          <div class="info-row">
            <span class="info-label">Technician ID:</span>
            <span class="info-value">{{ technician.technician_no }}</span>
          </div>

          <div class="info-row">
            <span class="info-label">Name:</span>
            <span class="info-value">{{ technician.name }}</span>
          </div>

          <div class="info-row">
            <span class="info-label">Registered Mobile:</span>
            <span class="info-value">{{ technician.mobile || technician.phone }}</span>
          </div>

          <div class="info-row avatar-row">
            <span class="info-label">Avatar:</span>
            <img :src="getAvatarUrl(technician.avatar)" alt="avatar" class="avatar-img" />
          </div>

          <div class="info-row">
            <span class="info-label">Role:</span>
            <span class="info-value">{{ formatRole(technician.work_role) }}</span>
          </div>

          <div v-if="technician.license_no" class="info-row licence-row">
            <span class="info-label"></span>
            <span class="licence-info">
              <img :src="checkIcon" alt="check" class="cert-icon" />
              {{ technician.license_no }}
            </span>
          </div>

          <div class="info-row">
            <span class="info-label">Status:</span>
            <span class="info-value status-text" :class="{ inactive: technician.status !== 1 }">
              {{ technician.status_text }}
            </span>
          </div>

          <div class="section-divider"></div>

          <div class="info-row">
            <span class="info-label">HM Care Orders:</span>
            <a href="#" class="order-count">{{ technician.order_stats?.hmc_orders || 0 }}</a>
          </div>
          <div class="info-row">
            <span class="info-label">Follow-up Orders:</span>
            <a href="#" class="order-count">{{ technician.order_stats?.fu_orders || 0 }}</a>
          </div>
          <div class="info-row">
            <span class="info-label">AD Orders:</span>
            <a href="#" class="order-count">{{ technician.order_stats?.ad_orders || 0 }}</a>
          </div>

          <div class="section-divider"></div>

          <div class="info-row">
            <span class="info-label">SIN/HST#:</span>
            <span class="info-value">{{ technician.sin_number || 'N/A' }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Residency Status:</span>
            <span class="info-value">{{ formatResidencyStatus(technician.residency_status) }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Address:</span>
            <span class="info-value">{{ technician.address || 'N/A' }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Email:</span>
            <span class="info-value">{{ technician.email || 'N/A' }}</span>
          </div>
      </div>

      <div class="detail-footer">
        <div class="section-divider"></div>
        <div class="detail-actions">
          <button
            v-if="technician.status === TECHNICIAN_LIST_STATUS_ACTIVE"
            class="action-btn"
            @click="handleDeactivate"
          >
            <span class="action-icon-wrap" aria-hidden="true">
              <img :src="deactivateIcon" alt="" class="action-icon-img action-icon-default" />
              <img :src="deactivateWhiteIcon" alt="" class="action-icon-img action-icon-hover" />
            </span>
            Deactivate
          </button>
          <button v-else class="action-btn action-btn--reactivate" @click="handleReactivate">
            <span class="action-icon-wrap" aria-hidden="true">
              <img :src="activateIcon" alt="" class="action-icon-img action-icon-default" />
              <img :src="activateWhiteIcon" alt="" class="action-icon-img action-icon-hover" />
            </span>
            Reactivate
          </button>
          <button class="action-btn" @click="handleEdit">
            <span class="action-icon-wrap" aria-hidden="true">
              <img :src="editIcon" alt="" class="action-icon-img action-icon-default" />
              <img :src="editWhiteIcon" alt="" class="action-icon-img action-icon-hover" />
            </span>
            Edit
          </button>
          <button class="action-btn" @click="handleUpdateMobile">
            <span class="action-icon-wrap" aria-hidden="true">
              <img :src="mobileIcon" alt="" class="action-icon-img action-icon-default" />
              <img :src="mobileWhiteIcon" alt="" class="action-icon-img action-icon-hover" />
            </span>
            Update Mobile
          </button>
        </div>
      </div>
    </div>

    <!-- Edit Technician Modal -->
    <EditTechnicians
      v-if="showEditModal"
      :technician-id="props.technicianId"
      @close="closeEditModal"
      @saved="handleEditSaved"
    />

    <!-- Mobile Status Modal (for Deactivate and Update Mobile) -->
    <MobileStatus
      v-if="showMobileStatusModal"
      :technician-id="props.technicianId"
      :mode="mobileStatusMode"
      :phone="technician.mobile || technician.phone"
      :phone-code="technician.phone_code"
      @close="closeMobileStatusModal"
      @saved="handleMobileStatusSaved"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { getTechnicianDetail, type TechnicianDetail } from '@/api/detailTechnicians'
import { TECHNICIAN_LIST_STATUS_ACTIVE } from '@/api/technicianslist'
import closeFillIcon from '@/assets/Fill 1@2x.png'
import deactivateIcon from '@/assets/Schedule.png'
import activateIcon from '@/assets/activate.png'
import editIcon from '@/assets/edit.png'
import mobileIcon from '@/assets/mobile.png'
import deactivateWhiteIcon from '@/assets/deactivate_white@2x (1).png'
import activateWhiteIcon from '@/assets/activate_white@2x.png'
import editWhiteIcon from '@/assets/edit_white@2x.png'
import mobileWhiteIcon from '@/assets/mobile_white@2x.png'
import checkIcon from '@/assets/normal@2x.png'
import EditTechnicians from './EditTechnicians.vue'
import MobileStatus from './MobileStatus.vue'

// Sub modal states
const showEditModal = ref(false)
const showMobileStatusModal = ref(false)
const mobileStatusMode = ref<'update' | 'deactivate' | 'reactivate'>('update')

const emit = defineEmits<{
  close: []
  updated: [technicianId: string]
}>()

const props = defineProps<{
  technicianId: string
}>()

const avatarBaseUrl = import.meta.env.VITE_BASE_URL || ''

const technician = ref<TechnicianDetail | null>(null)

function getAvatarUrl(avatar: string): string {
  if (!avatar || avatar.trim() === '') return ''
  if (avatar.startsWith('http')) return avatar
  return `${avatarBaseUrl}${avatar}`
}

function formatRole(role: string): string {
  if (!role) return ''
  return role.charAt(0).toUpperCase() + role.slice(1)
}

function formatResidencyStatus(status: string): string {
  if (!status) return 'N/A'
  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

async function fetchDetail() {
  if (!props.technicianId) {
    emit('close')
    return
  }

  try {
    const response = await getTechnicianDetail(props.technicianId)

    if (response.code === 200) {
      technician.value = response.data
    }
  } catch (err) {
    console.error('Failed to fetch technician detail:', err)
  }
}

function handleClose(): void {
  emit('close')
}

function handleEdit(): void {
  showEditModal.value = true
}

function closeEditModal(): void {
  showEditModal.value = false
}

function handleEditSaved(): void {
  showEditModal.value = false
  void fetchDetail()
  emit('updated', props.technicianId)
}

function handleDeactivate(): void {
  mobileStatusMode.value = 'deactivate'
  showMobileStatusModal.value = true
}

function handleReactivate(): void {
  mobileStatusMode.value = 'reactivate'
  showMobileStatusModal.value = true
}

function closeMobileStatusModal(): void {
  showMobileStatusModal.value = false
}

function handleMobileStatusSaved(): void {
  showMobileStatusModal.value = false
  void fetchDetail()
  emit('updated', props.technicianId)
}

function handleUpdateMobile(): void {
  mobileStatusMode.value = 'update'
  showMobileStatusModal.value = true
}

watch(
  () => props.technicianId,
  (newId) => {
    if (newId) {
      fetchDetail()
    }
  },
  { immediate: true },
)

onMounted(() => {
  if (props.technicianId) {
    fetchDetail()
  }
})
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  padding: 24px 24px 45px 24px;
  z-index: 10000;
  box-sizing: border-box;
}

.detail-container {
  background-color: #ffffff;
  border-radius: 12px;
  width: 540px;
  max-width: calc(100% - 48px);
  height: calc(100vh - 69px);
  max-height: calc(100vh - 69px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: #ffffff;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0 24px;
  flex-shrink: 0;
  height: auto;
}

.detail-title {
  font-size: 16px;
  font-weight: 600;
  font-family:
    'SF Pro',
    SFPro,
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  margin: 24px 0 16px;
  text-align: left;
}

.close-btn {
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #000000;
  border-radius: 4px;
}



.close-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
  margin-top: 24px;
}

.modal-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  scrollbar-gutter: stable;
}

.modal-body::-webkit-scrollbar {
  width: 6px;
}

.modal-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.modal-body::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

.error-state {
  text-align: center;
  padding: 40px;
  color: #999;
}

.btn-retry {
  margin-top: 16px;
  padding: 8px 24px;
  border: 1px solid #e0e0e0;
  background: #fff;
  border-radius: 20px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}


.info-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.info-label {
  width: 160px;
  text-align: right;
  font-size: 0.9rem;
  color: #000000;
  font-weight: 500;
  flex-shrink: 0;
}

.info-value {
  font-size: 0.9rem;
  color: #000000;
  font-weight: 400;
  white-space: normal;
  word-break: break-word;
}

.avatar-row {
  align-items: center;
}

.avatar-img {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
}

.licence-row {
  margin-top: -8px;
}

.licence-info {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.85rem;
  color: #000000;
}

.cert-icon {
  width: 12px;
  height: 12px;
  object-fit: contain;
}

.status-text {
  color: #00aeef;
  font-weight: 600;
}

.status-text.inactive {
  color: #fb0101;
}

.section-divider {
  width: 100%;
  height: 1px;
  background: #e0e0e0;
  flex-shrink: 0;
  align-self: stretch;
}

.order-count {
  color: #000000;
  text-decoration: underline;
  font-weight: 400;
  cursor: default;
}

.order-count:hover {
  color: #000000;
  text-decoration: underline;
  background-color: transparent !important;
}

.detail-footer {
  flex-shrink: 0;
  padding: 16px 24px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.detail-actions {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding: 0 0 16px;
  background-color: #ffffff;
  flex-shrink: 0;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 16px;
  height: 28px;
  border: 1px solid #000000;
  background: #fff;
  border-radius: 16px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 400;
  line-height: 28px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  transition:
    background-color 0.2s ease,
    color 0.2s ease,
    border-color 0.2s ease;
}

.action-btn:hover {
  background: #000000;
  border-color: #000000;
  color: #ffffff;
}

.action-icon-wrap {
  position: relative;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.action-icon-img {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.action-icon-wrap .action-icon-default,
.action-icon-wrap .action-icon-hover {
  position: absolute;
  top: 0;
  left: 0;
  transition: opacity 0.2s ease;
}

.action-icon-wrap .action-icon-hover {
  opacity: 0;
}

.action-btn:hover .action-icon-wrap .action-icon-default {
  opacity: 0;
}

.action-btn:hover .action-icon-wrap .action-icon-hover {
  opacity: 1;
}

@media (max-width: 768px) {
  .modal-overlay {
    padding: 10px;
  }

  .info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .info-label {
    width: 100%;
    text-align: left;
  }

  .modal-body {
    padding: 16px 24px 24px;
  }
}
</style>
